
import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const languages = [
  { code: 'no', name: 'Norsk', flag: '🇳🇴' },
  { code: 'en', name: 'English', flag: '🇬🇧' }
];

export function LanguageSwitcher() {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.focus();
    } else if (e.key === 'ArrowDown' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const handleLanguageSelect = (langCode: string) => {
    i18n.changeLanguage(langCode);
    setIsOpen(false);
    buttonRef.current?.focus();
  };

  const handleOptionKeyDown = (e: React.KeyboardEvent, langCode: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleLanguageSelect(langCode);
    }
  };

  return (
    <div className="relative" ref={dropdownRef} onKeyDown={handleKeyDown}>
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer focus:ring-2 focus:ring-fuchsia-500 focus:ring-offset-2"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={`Velg språk. Nåværende: ${currentLanguage.name}`}
      >
        <span className="text-lg" aria-hidden="true">{currentLanguage.flag}</span>
        <span className="text-sm font-medium text-slate-700">{currentLanguage.code.toUpperCase()}</span>
        <i className={`ri-arrow-down-s-line text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} aria-hidden="true"></i>
      </button>

      {isOpen && (
        <div 
          className="absolute top-full right-0 mt-2 bg-white rounded-lg shadow-lg border border-slate-200 py-1 min-w-[140px] z-50"
          role="listbox"
          aria-label="Tilgjengelige språk"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageSelect(lang.code)}
              onKeyDown={(e) => handleOptionKeyDown(e, lang.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors cursor-pointer text-left ${
                lang.code === i18n.language ? 'bg-slate-50' : ''
              }`}
              role="option"
              aria-selected={lang.code === i18n.language}
            >
              <span className="text-lg" aria-hidden="true">{lang.flag}</span>
              <span className="text-sm text-slate-700">{lang.name}</span>
              {lang.code === i18n.language && (
                <i className="ri-check-line text-fuchsia-500 ml-auto" aria-hidden="true"></i>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
